import { CloseSquare, SearchNormal1 } from 'iconsax-react'
import { useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import AvatarPending from '@/assets/Avatar-pending.svg?react'
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import type {
  EUserRole,
  EUserRoleSelector,
} from '@/hooks/usePermissions/permissions.type'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'
import { useUpdateEnterpriseUserRoleMutation } from '@/store/api/Enterprise/Enterprise.query'
import {
  useInviteMemberToATeamMutation,
  useRemoveMemberFromTeamMutation,
} from '@/store/api/Workspace/Workspace.query'
import type {
  MemberToAssign,
  UserData,
  Workspace,
} from '@/store/api/Workspace/Workspace.types'
import { getColorByName } from '@/utils/colorByName'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { DobleAvatarPlusNameCard } from './DobleAvatarPlusNameCard'
import { EditMemberCard } from './EditMemberCard'
import { MemberItem } from './MemberItem'
import { PopUpTopHeader } from './PopUpTopHeader'

interface ManageTeamsFormValues {
  teams: Record<string, string[]>
  role?: string
}

export const ManageOffice = ({
  isOpen,
  setIsOpen,
  workspace,
}: {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  workspace: Workspace
}) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const isSmallMobile = useSmallMobileMediaQuery()

  const [showMore, setShowMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const [selectedUser, setSelectedUser] = useState<UserData | null>(null)

  const [selectedRole, setSelectedRole] = useState<
    EUserRoleSelector | 'remove' | null
  >(null)

  const [inviteMemberToATeam, { isLoading: isInvitingMember }] =
    useInviteMemberToATeamMutation()
  const [removeMemberFromTeam, { isLoading: isRemovingMember }] =
    useRemoveMemberFromTeamMutation()
  const [updateMemberRole, { isLoading: isUpdatingMemberRole }] =
    useUpdateEnterpriseUserRoleMutation()

  const form = useForm<ManageTeamsFormValues>({
    defaultValues: {
      teams: {},
      role: selectedUser?.user?.role,
    },
  })

  useEffect(() => {
    if (selectedUser?.user) {
      const currentTeams = workspace.teams
        .filter(
          (team) =>
            team.members?.some(
              (member) => member.userEmail === selectedUser.userEmail,
            ),
        )
        .reduce(
          (acc, team) => {
            if (!acc[workspace._id]) {
              acc[workspace._id] = []
            }
            acc[workspace._id].push(team._id)
            return acc
          },
          {} as Record<string, string[]>,
        )

      form.reset({
        teams: currentTeams,
        role: selectedUser.user.role,
      })
    }
  }, [selectedUser, workspace])

  const handleRoleChange = (role: EUserRoleSelector) => {
    setSelectedRole(role)
  }

  const handleShowMore = () => {
    setShowMore(!showMore)
  }

  const closeModal = () => {
    setIsOpen(false)
    setShowMore(false)
    setSearchTerm('')
    setSelectedUser(null)
    setSelectedRole(null)
  }

  const handleTeamAssignment = async () => {
    if (!selectedUser?.userEmail) {
      toast.error(t('dashboard.company.manageModal.emailRequired'))
      return
    }

    try {
      const formValues = form.getValues()
      const teamIds = formValues.teams[workspace._id] || []

      const memberData: MemberToAssign = {
        email: selectedUser.userEmail,
        role: selectedUser.user?.role ?? '',
        workspaces: [
          {
            workspaceId: workspace._id,
            teamIds,
          },
        ],
      }

      await inviteMemberToATeam(memberData).unwrap()

      const currentTeams = workspace.teams
        .filter(
          (team) =>
            team.members?.some(
              (member) => member.userEmail === selectedUser.userEmail,
            ),
        )
        .map((team) => team._id)

      const teamsToRemove = currentTeams.filter(
        (teamId) => !teamIds.includes(teamId),
      )

      if (teamsToRemove.length > 0) {
        await removeMemberFromTeam({
          workspaces: [
            {
              workspaceId: workspace._id,
              teamIds: teamsToRemove,
            },
          ],
          memberId: selectedUser.user?._id ?? '',
        }).unwrap()
      }

      toast.success(t('dashboard.company.manageModal.memberTeamUpdated'))
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('dashboard.company.manageModal.memberTeamUpdatedError'))
    }
  }

  const handleRemoveMember = async (userId: string) => {
    const formValues = form.getValues()
    const teamIds = formValues.teams[workspace._id] || []

    try {
      await removeMemberFromTeam({
        workspaces: [
          {
            workspaceId: workspace._id,
            teamIds,
          },
        ],
        memberId: userId,
      }).unwrap()

      toast.success(t('dashboard.company.manageModal.memberRemoved'))
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('dashboard.company.manageModal.errorRemovingMember'))
    }
  }

  const handleUpdateMemberRole = async () => {
    if (!selectedUser?.user?._id) return

    try {
      await updateMemberRole({
        id: selectedUser?.user?._id ?? '',
        role: selectedRole ?? '',
      }).unwrap()
      toast.success(t('dashboard.company.manageModal.memberRoleUpdated'))
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('dashboard.company.manageModal.memberRoleUpdatedError'))
    }
  }

  const handleSaveEditMember = async () => {
    if (selectedRole && selectedRole !== selectedUser?.user?.role) {
      if (selectedRole === 'remove') {
        await handleRemoveMember(selectedUser?._id ?? '')
      } else {
        await handleUpdateMemberRole()
      }
    }

    const formValues = form.getValues()
    if (Object.keys(formValues.teams).length > 0) {
      await handleTeamAssignment()
    }

    setSelectedUser(null)
    setSelectedRole(null)
    form.reset()
  }

  const handleEditMember = (user: UserData) => {
    setSelectedUser(user)
    setShowMore(true)

    if (user?.user?.role) {
      setSelectedRole(user.user.role as EUserRoleSelector)
    }
  }

  const filterMembers = (user: UserData) => {
    const { user: userData, userEmail } = user
    const firstName = userData?.firstname ?? ''
    const lastName = userData?.lastname ?? ''

    const fullName = `${firstName} ${lastName}`

    return (
      fullName.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      userEmail.toLowerCase().includes(searchTerm?.toLowerCase())
    )
  }

  const isLoading = isRemovingMember || isInvitingMember || isUpdatingMemberRole

  const nameShowInOthers =
    workspace?.allMembers?.[2]?.user?.firstname ??
    workspace?.allMembers?.[2]?.userEmail

  const showMoreMembers =
    !selectedUser &&
    !showMore &&
    workspace?.allMembers?.length &&
    workspace?.allMembers?.length > 2
  const showPreviewMembers = workspace?.allMembers && showMore && !selectedUser

  return (
    <AlertDialog open={isOpen} onOpenChange={closeModal}>
      <AlertDialogContent
        className={`flex flex-col gap-3 ${
          isMobile ? 'w-[350px]' : isSmallMobile ? 'w-[250px]' : 'w-[500px]'
        }`}
      >
        <div className="flex flex-1 flex-col gap-3">
          {/* Modal Home */}
          {!showMore && (
            <>
              <div className="flex flex-row justify-between items-center">
                <p className="text-base font-bold">{workspace?.name}</p>

                <div className="flex flex-row justify-end items-center">
                  <Button
                    onClick={closeModal}
                    variant="ghost"
                    className="text-base"
                  >
                    <CloseSquare
                      width={18}
                      height={17}
                      variant="Bold"
                      color={weCheckPalette.neutralColor}
                    />
                  </Button>
                </div>
              </div>

              {/* Invite Member */}

              {/* TODO: Fix the backend to handle multiple invites
                Seem like the backend is not handling the multiple invites
                correctly for concurrency request to MongoDB.
                This is a temporary fix to avoid the issue.
              */}

              {/* <div className="flex flex-col justify-between gap-4">
                <div>
                  <p className="text-neutralColor60 text-sml">
                    {t('dashboard.company.manageModal.memberWillJoinAllTeams')}
                  </p>
                </div>
                <InviteMemberForm
                  onInvite={handleInviteMember}
                  isLoading={isInvitingMember}
                />
              </div> */}

              {/* Preview Members */}
              {workspace?.allMembers?.length ? (
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="text-neutralColor60 text-sml">
                      {t('dashboard.company.manageModal.whoHasAccess')}
                    </p>
                  </div>

                  {workspace?.allMembers?.slice(0, 2).map((user, index) => (
                    <MemberItem
                      key={`${user._id}-${index}`}
                      firstname={user.user?.firstname ?? ''}
                      lastname={user.user?.lastname ?? ''}
                      email={user.userEmail}
                      invite={user.invite}
                      role={user.user?.role ?? ''}
                      color={getColorByName(
                        user.user?.firstname ?? user.userEmail,
                        user.user?.lastname ?? '',
                      )}
                      onClick={() => {
                        setSelectedUser(user)
                        setSelectedRole(user.user?.role as EUserRoleSelector)
                        setShowMore(true)
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}

          {/* Show more members */}
          {showMoreMembers ? (
            <div className="flex flex-1 flex-col">
              <button
                onClick={handleShowMore}
                className="flex flex-1 text-neutralColor"
              >
                {showMore ? (
                  ''
                ) : (
                  <div className="flex flex-1 my-4">
                    <DobleAvatarPlusNameCard
                      nameShowInOthers={nameShowInOthers}
                      countElements={workspace?.allMembers?.length}
                      rightIcon
                      isTeamIcon
                    />
                  </div>
                )}
              </button>
            </div>
          ) : null}

          {/* View list of all members */}
          {showPreviewMembers && (
            <div className="flex flex-1 flex-col">
              <PopUpTopHeader
                text={`${workspace?.allMembers?.length} ${t(
                  'dashboard.company.manageModal.colaboratorsOffice',
                )}`}
                onClick={() => {
                  setShowMore(false)
                  setSearchTerm('')
                }}
                setIsOpen={closeModal}
              />

              <Input
                placeholder={t('dashboard.company.search')}
                className="h-[46px] rounded-lg w-full p-4 mb-2 mt-2"
                icon={
                  <SearchNormal1
                    className="absolute mt-6 mr-4"
                    size="18"
                    color={weCheckPalette.neutralColor30}
                  />
                }
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <div className="flex flex-1 flex-col max-h-[330px] overflow-y-auto gap-3 mt-4 scrollbar-thin scrollbar-thumb-neutralColor60 scrollbar-track-transparent">
                {workspace?.allMembers
                  ?.filter(filterMembers)
                  .map((user, index) => (
                    <div key={`${user._id}-member-item-${index}`}>
                      <MemberItem
                        key={`${user.userEmail}`}
                        firstname={user.user?.firstname ?? user.userEmail}
                        lastname={user.user?.lastname ?? ''}
                        invite={user.invite}
                        email={user.userEmail}
                        role={user.user?.role ?? ''}
                        onClick={() => handleEditMember(user)}
                        color={getColorByName(
                          user.user?.firstname ?? user.userEmail,
                          user.user?.lastname ?? '',
                        )}
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Edit Selected User View*/}
          {selectedUser && (
            <div className="flex flex-1 flex-col gap-4">
              {!selectedUser?.invite ? (
                <>
                  <PopUpTopHeader
                    text={t('dashboard.company.manageModal.memberSettings')}
                    onClick={() => setSelectedUser(null)}
                    setIsOpen={closeModal}
                  />

                  <FormProvider {...form}>
                    <EditMemberCard
                      color={getColorByName(
                        selectedUser.user?.firstname ?? selectedUser.userEmail,
                        selectedUser.user?.lastname ?? '',
                      )}
                      role={selectedUser.user?.role as EUserRole}
                      firstname={selectedUser.user?.firstname ?? ''}
                      lastname={selectedUser.user?.lastname ?? ''}
                      email={selectedUser.userEmail}
                      workspaces={[workspace]}
                      onRoleChange={handleRoleChange}
                    />
                  </FormProvider>

                  <div className="flex flex-1 flex-col justify-end">
                    <div className="flex flex-row gap-3 justify-end pt-3">
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedUser(null)}
                        disabled={isLoading}
                      >
                        {t('dashboard.company.manageModal.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSaveEditMember}
                        isLoading={isLoading}
                      >
                        {t('dashboard.company.manageModal.save')}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <PopUpTopHeader
                    text={t('dashboard.company.editInvite.inviteSettings')}
                    onClick={() => setSelectedUser(null)}
                    setIsOpen={closeModal}
                  />

                  <div className="flex flex-row justify-between items-center">
                    <div className="flex flex-row items-center gap-3">
                      <Avatar>
                        <AvatarPending />
                      </Avatar>

                      <div className="flex flex-col">
                        <p className="text-sm text-neutralColor60">
                          {selectedUser?.userEmail}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
